import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/PersonAddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useAuth } from '@/contexts/AuthContext';
import { PERMISSIONS, getInitials } from '@/types/auth';
import type { UserDto } from '@/types/auth';
import type { CreateUserRequest, UpdateUserRequest } from '@/types/administration';
import UserFormDialog from './UserFormDialog';
import type { UserFormValues } from './UserFormDialog';
import RolesPermissionsMatrix from './RolesPermissionsMatrix';

export default function UserManagementPage() {
  const { t } = useTranslation('userManagement');
  // User administration is company-admin-only — a Branch- or Warehouse-scoped user can still open
  // the list (their UserManage permission is unchanged) but gets no create/edit/lock/delete
  // affordances, matching the server-side guard in UserService.RequireCompanyScopedActor.
  const { user: currentUser } = useAuth();
  const canManageUsers = (currentUser?.accessScope ?? 'Company') === 'Company';
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await apiClient.get<UserDto[]>('/users')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const saveMutation = useMutation({
    mutationFn: (values: UserFormValues) => {
      if (editingUser) {
        const payload: UpdateUserRequest = {
          email: values.email, firstName: values.firstName, lastName: values.lastName,
          departmentId: values.departmentId,
          accessScope: values.accessScope, branchId: values.branchId, defaultWarehouseId: values.defaultWarehouseId,
          roleIds: values.roleIds, isActive: values.isActive, clientId: values.clientId,
        };
        return apiClient.put(`/users/${editingUser.id}`, payload);
      }
      const payload: CreateUserRequest = {
        username: values.username, email: values.email, password: values.password ?? '',
        firstName: values.firstName, lastName: values.lastName, departmentId: values.departmentId,
        accessScope: values.accessScope, branchId: values.branchId, defaultWarehouseId: values.defaultWarehouseId,
        roleIds: values.roleIds, clientId: values.clientId,
      };
      return apiClient.post('/users', payload);
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditingUser(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/users/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const lockMutation = useMutation({
    mutationFn: ({ id, lock }: { id: string; lock: boolean }) => apiClient.post(`/users/${id}/${lock ? 'lock' : 'unlock'}`),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<UserDto>[] = [
    {
      field: 'fullName',
      headerName: t('table.user'),
      render: (r) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip label={getInitials(r)} size="small" sx={{ bgcolor: r.avatarColor, color: '#fff', fontWeight: 700 }} />
          <Stack>
            <Typography variant="body2" fontWeight={600}>
              {r.fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {r.email}
            </Typography>
          </Stack>
        </Stack>
      ),
      searchValue: (r) => `${r.fullName} ${r.email}`,
    },
    { field: 'roles', headerName: t('table.role'), render: (r) => r.roles.join(', ') || '—' },
    { field: 'departmentName', headerName: t('table.department'), render: (r) => r.departmentName ?? '—', filterable: true },
    {
      // What this user can actually reach inside the company — the scope plus whichever
      // branch/warehouse it resolves to, so the mapping is readable without opening the row.
      field: 'accessScope',
      headerName: t('table.accessScope'),
      filterable: true,
      render: (r) => {
        const scope = r.accessScope ?? 'Company';
        const target = scope === 'Warehouse' ? r.defaultWarehouseName : scope === 'Branch' ? r.branchName : null;
        return (
          <Stack>
            <Typography variant="body2">{t(`form.accessScopes.${scope}`)}</Typography>
            {target && (
              <Typography variant="caption" color="text.secondary">
                {target}
              </Typography>
            )}
          </Stack>
        );
      },
      searchValue: (r) => `${r.accessScope ?? 'Company'} ${r.branchName ?? ''} ${r.defaultWarehouseName ?? ''}`,
    },
    {
      field: 'status',
      headerName: t('table.status'),
      render: (r) => <StatusChip status={r.isLocked ? t('status.locked') : r.isActive ? t('status.active') : t('status.inactive')} />,
      searchValue: (r) => (r.isLocked ? t('status.locked') : r.isActive ? t('status.active') : t('status.inactive')),
      filterable: true,
    },
    { field: 'lastLoginDate', headerName: t('table.lastLogin'), render: (r) => (r.lastLoginDate ? new Date(r.lastLoginDate).toLocaleString() : '—') },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) =>
        canManageUsers ? (
          <IfPermitted permission={PERMISSIONS.USER_MANAGE}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => {
                  setEditingUser(r);
                  setDialogOpen(true);
                }}
              >
                Edit
              </Button>
              <Button size="small" onClick={() => lockMutation.mutate({ id: r.id, lock: !r.isLocked })}>
                {r.isLocked ? t('actions.unlock') : t('actions.lock')}
              </Button>
              <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
                Delete
              </Button>
            </Stack>
          </IfPermitted>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          canManageUsers ? (
            <IfPermitted permission={PERMISSIONS.USER_MANAGE}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingUser(null);
                  setDialogOpen(true);
                }}
              >
                {t('addUser')}
              </Button>
            </IfPermitted>
          ) : undefined
        }
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('tabs.users')} />
        <Tab label={t('tabs.roles')} />
      </Tabs>

      {tab === 0 ? (
        <DataTable columns={columns} rows={users} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="users" />
      ) : (
        <RolesPermissionsMatrix />
      )}

      <UserFormDialog
        open={dialogOpen}
        user={editingUser}
        onClose={() => {
          setDialogOpen(false);
          setEditingUser(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteUser.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
